import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  InputAdornment,
  Stack,
  Tab,
  Tabs,
  TextField,
  Divider,
  IconButton,
  Tooltip,
  MenuItem,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import {
  CheckCircle as CheckCircleIcon,
  DeleteForever as DeleteForeverIcon,
  Flag as FlagIcon,
  Block as BlockIcon,
  Cancel as CancelIcon,
  Pending as PendingIcon,
  Refresh as RefreshIcon,
  Restore as RestoreIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Insights as InsightsIcon,
  Payments as PaymentsIcon,
  VerifiedUser as VerifiedUserIcon,
  GppBad as GppBadIcon,
  Public as PublicIcon,
  PublicOff as PublicOffIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import axios from 'axios';
import DashboardLayout from '../../layouts/DashboardLayout';
import TestingLoginsPanel from '../../components/Franchisee/TestingLoginsPanel';
import AssistedRegistrationForm from '../../components/Franchisee/AssistedRegistrationForm';
import { useAuth } from '../../contexts/AuthContext';
import {
  BUSINESS_TYPE_RELEVANT_FEATURES,
  getMergedFranchiseFeatureCatalog,
  getFeatureMeta,
  normalizeBusinessTypeKey,
} from '../../config/franchiseeFeatures';
import { getBusinessTypeLabel } from '../../config/franchiseeBusinessTypes';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

const generateTemporaryPin = () => {
  const min = 10000000;
  const max = 100000000;
  return String(Math.floor(Math.random() * (max - min)) + min);
};

const buildVyaparAutoLoginUrl = (phone, pin) => {
  if (typeof window === 'undefined') {
    return '';
  }

  const loginUrl = new URL('/locsydashboard', window.location.origin);
  if (phone) {
    loginUrl.searchParams.set('phone', phone);
  }
  if (pin) {
    loginUrl.searchParams.set('pin', pin);
  }
  loginUrl.searchParams.set('autoLogin', '1');
  return loginUrl.toString();
};

const buildWhatsAppUrl = (phone, message) => {
  const cleanPhone = String(phone || '').replace(/\D/g, '').slice(-10);
  const encodedMessage = encodeURIComponent(message);
  return `https://wa.me/91${cleanPhone}?text=${encodedMessage}`;
};

const buildAccessMessage = ({ admin, pin, loginUrl }) => {
  const businessName = resolveBusinessName(admin);

  return [
    `Welcome to Loksy Vyapar — ${businessName}.`,
    `Vyapar ID: ${admin.phone}`,
    `Temporary Vyapar PIN: ${pin}`,
    `Open your dashboard (auto-login): ${loginUrl}`,
    '',
    'Open the link to sign in automatically. After login, change your PIN from Profile.',
    '',
    'లోక్సీ వ్యాపార్‌కు స్వాగతం. పై లింక్‌ను ఓపెన్ చేసి ఆటోమేటిక్‌గా లాగిన్ అవ్వండి.',
  ].join('\n');
};

const parseRequestedFeatures = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch (err) {
      return [];
    }
  }
  return [];
};

/** Enabled feature keys for the Review / Save features dialog. */
const resolveEnabledFeatureKeys = (admin, allAdminsList = []) => {
  const fromRows = (rows) =>
    (rows || [])
      .filter((f) => {
        if (!f || typeof f !== 'object') return false;
        if (!('is_enabled' in f)) return true;
        return (
          f.is_enabled === true ||
          f.is_enabled === 1 ||
          f.is_enabled === 't' ||
          f.is_enabled === 'true'
        );
      })
      .map((f) => f.feature_name)
      .filter((key) => typeof key === 'string' && key.trim())
      .map((key) => key.trim());

  const direct = fromRows(admin?.features);
  if (direct.length) return direct;

  const matched = allAdminsList.find((row) => row?.id && row.id === admin?.id);
  const fromAll = fromRows(matched?.features);
  if (fromAll.length) return fromAll;

  const requested = parseRequestedFeatures(
    admin?.requested_features || matched?.requested_features,
  ).filter(Boolean);
  if (requested.length) return requested;

  const categorySource = admin?.business_categories || matched?.business_categories;
  let categories = [];
  if (Array.isArray(categorySource)) {
    categories = categorySource;
  } else if (typeof categorySource === 'string' && categorySource.trim()) {
    try {
      const parsed = JSON.parse(categorySource);
      categories = Array.isArray(parsed) ? parsed : [];
    } catch {
      categories = [];
    }
  }
  if (!categories.length) {
    const typeKey = normalizeBusinessTypeKey(
      admin?.business_type || matched?.business_type || '',
    );
    if (typeKey) categories = [typeKey];
  }

  const fromTypes = new Set();
  for (const value of categories) {
    const key = normalizeBusinessTypeKey(value);
    const relevant = BUSINESS_TYPE_RELEVANT_FEATURES[key] || [];
    relevant.forEach((feature) => fromTypes.add(feature));
  }
  return [...fromTypes];
};

const formatAdminCategories = (admin) => {
  if (!admin) return '-';
  let categories = [];
  if (Array.isArray(admin.business_categories)) {
    categories = admin.business_categories;
  } else if (typeof admin.business_categories === 'string' && admin.business_categories.trim()) {
    try {
      const parsed = JSON.parse(admin.business_categories);
      categories = Array.isArray(parsed) ? parsed : [];
    } catch {
      categories = [];
    }
  }
  if (!categories.length && admin.business_type) {
    categories = [admin.business_type];
  }
  if (!categories.length) return '-';
  return categories.map((value) => getBusinessTypeLabel(value)).join(' · ');
};

const resolveBusinessName = (admin) => {
  if (!admin) return '-';
  if (admin.business_name && String(admin.business_name).trim()) return admin.business_name;
  if (admin.contact_person_name && String(admin.contact_person_name).trim()) return admin.contact_person_name;
  if (admin.email && String(admin.email).includes('@')) return String(admin.email).split('@')[0];
  return '-';
};

const getAdminDisplayStatus = (admin) => {
  const lifecycleStatus = String(admin?.lifecycle_status || '').trim().toLowerCase();
  if (lifecycleStatus === 'suspended' || lifecycleStatus === 'flagged') {
    return lifecycleStatus;
  }
  return admin?.approval_status || 'pending';
};

const ADMIN_TABS = [
  { key: 'active', label: 'Active', statuses: ['approved', 'pending', 'rejected'] },
  { key: 'suspended', label: 'Paused', statuses: ['suspended'] },
  { key: 'flagged', label: 'Recycle Bin', statuses: ['flagged'] },
];

const USAGE_PERIOD_TABS = [
  { key: 'today', label: 'Today' },
  { key: 'week', label: 'This week' },
  { key: 'month', label: 'This month' },
  { key: 'year', label: 'This year' },
  { key: 'all', label: 'All time' },
];

const formatUsageCurrency = (value) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(Number(value) || 0);

const formatUsageDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
};

const ACTIVITY_TIER_META = {
  active: { label: 'Active', color: 'success' },
  low: { label: 'Low usage', color: 'warning' },
  dormant: { label: 'Dormant', color: 'default' },
  never: { label: 'Never used', color: 'error' },
};

const buildUsageChipLabel = (stats, periodKey = 'month') => {
  if (!stats?.periods?.[periodKey]) return 'No data';
  const period = stats.periods[periodKey];
  const tier = ACTIVITY_TIER_META[stats.activityTier] || ACTIVITY_TIER_META.never;
  const bills = period.billCount || 0;
  const amount = formatUsageCurrency(period.totalAmount);
  return `${tier.label} · ${bills} bills · ${amount}`;
};

const parsePricingSnapshot = (admin) => {
  const raw = admin?.registration_pricing_snapshot;
  if (!raw) return null;
  if (typeof raw === 'object' && raw !== null) return raw;
  if (typeof raw === 'string' && raw.trim()) {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
  return null;
};

const formatPaise = (paise) => {
  const n = Number(paise);
  if (!Number.isFinite(n)) return '—';
  return `₹${(n / 100).toFixed(2)}`;
};

const getRegistrationServiceLines = (admin) => {
  const snapshot = parsePricingSnapshot(admin);
  if (Array.isArray(snapshot?.lines) && snapshot.lines.length > 0) {
    return snapshot.lines.map((line) => ({
      key: line.feature_key,
      label: line.display_label || getFeatureMeta(line.feature_key).label,
      amountPaise: Number(line.amount_paise) || 0,
      tenureDays: line.tenure_days,
      pricingMode: line.pricing_mode || null,
      catalogAmountPaise:
        line.catalog_amount_paise != null
          ? Number(line.catalog_amount_paise)
          : null,
    }));
  }
  return parseRequestedFeatures(admin?.requested_features).map((key) => ({
    key,
    label: getFeatureMeta(key).label,
    amountPaise: null,
    tenureDays: null,
    pricingMode: null,
    catalogAmountPaise: null,
  }));
};

/** Seed Default/Free/Custom controls from snapshot + catalog defaults. */
const NON_BILLABLE_FEATURE_KEYS = new Set(['whatsapp_config', 'export_catalog']);

const buildInitialFeaturePricing = (admin, features, catalogByKey = {}) => {
  const snapshot = parsePricingSnapshot(admin);
  const overrides =
    snapshot?.admin_pricing_overrides &&
    typeof snapshot.admin_pricing_overrides === 'object'
      ? snapshot.admin_pricing_overrides
      : {};
  const lineByKey = {};
  (Array.isArray(snapshot?.lines) ? snapshot.lines : []).forEach((line) => {
    if (line?.feature_key) lineByKey[line.feature_key] = line;
  });
  const hasBilling =
    features.includes('smartbill') || features.includes('cart_model_bill');
  const pricing = {};
  features.forEach((key) => {
    if (NON_BILLABLE_FEATURE_KEYS.has(key)) {
      pricing[key] = { mode: 'free', amountRupees: '' };
      return;
    }
    const override = overrides[key];
    const line = lineByKey[key];
    if (override?.mode === 'free' || override?.mode === 'custom' || override?.mode === 'default') {
      pricing[key] = {
        mode: override.mode,
        amountRupees:
          override.mode === 'custom'
            ? String(((Number(override.amount_paise) || 0) / 100).toFixed(2))
            : '',
      };
      return;
    }
    if (line?.pricing_mode === 'custom') {
      pricing[key] = {
        mode: 'custom',
        amountRupees: String(((Number(line.amount_paise) || 0) / 100).toFixed(2)),
      };
      return;
    }
    if (
      line?.pricing_mode === 'free' ||
      (key === 'view_bills' && hasBilling) ||
      (key === 'view_bills' && Number(line?.amount_paise) === 0)
    ) {
      pricing[key] = { mode: 'free', amountRupees: '' };
      return;
    }
    pricing[key] = { mode: 'default', amountRupees: '' };
  });
  return pricing;
};

const resolvePreviewFeaturePaise = (key, features, pricing, catalogByKey = {}) => {
  if (NON_BILLABLE_FEATURE_KEYS.has(key)) return 0;
  const entry = pricing?.[key] || { mode: 'default' };
  if (entry.mode === 'free') return 0;
  if (entry.mode === 'custom') {
    const rupees = Number(entry.amountRupees);
    if (!Number.isFinite(rupees) || rupees < 0) return 0;
    return Math.round(rupees * 100);
  }
  const catalogPaise = Number(catalogByKey[key]?.amount_paise) || 0;
  const hasBilling =
    features.includes('smartbill') || features.includes('cart_model_bill');
  if (key === 'view_bills' && hasBilling) return 0;
  return catalogPaise;
};

const toApiFeaturePricing = (pricing) => {
  const out = {};
  Object.entries(pricing || {}).forEach(([key, entry]) => {
    const mode = entry?.mode || 'default';
    if (mode === 'custom') {
      const rupees = Number(entry.amountRupees);
      out[key] = {
        mode: 'custom',
        amount_paise: Number.isFinite(rupees) && rupees > 0 ? Math.round(rupees * 100) : 0,
      };
    } else if (mode === 'free') {
      out[key] = { mode: 'free', amount_paise: 0 };
    } else {
      out[key] = { mode: 'default' };
    }
  });
  return out;
};

const getRegistrationPaidAt = (admin) => {
  if (admin?.registration_paid_at) return admin.registration_paid_at;
  if (String(admin?.registration_payment_status || '').toLowerCase() === 'paid') {
    return admin?.updated_at || admin?.created_at || null;
  }
  return null;
};

const registrationPaymentStatusColor = (status) => {
  switch (String(status || '').toLowerCase()) {
    case 'paid':
      return 'success';
    case 'unpaid':
      return 'warning';
    case 'pending_checkout':
      return 'info';
    default:
      return 'default';
  }
};

const hasRegistrationFee = (admin) => {
  const total = Number(admin?.registration_total_paise) || 0;
  const status = String(admin?.registration_payment_status || '').toLowerCase();
  return total > 0 || (status && status !== 'not_applicable');
};

const renderRegistrationServices = (admin, { maxChips = 4 } = {}) => {
  const lines = getRegistrationServiceLines(admin);
  if (!lines.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }
  const visible = lines.slice(0, maxChips);
  const remaining = lines.length - visible.length;
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {visible.map((line) => (
        <Chip
          key={line.key}
          label={
            line.amountPaise != null && line.amountPaise > 0
              ? `${line.label} (${formatPaise(line.amountPaise)})`
              : line.label
          }
          size="small"
          variant="outlined"
        />
      ))}
      {remaining > 0 && (
        <Chip label={`+${remaining} more`} size="small" variant="outlined" />
      )}
    </Box>
  );
};

const searchableAdminText = (admin) =>
  [
    admin?.phone,
    admin?.email,
    resolveBusinessName(admin),
    admin?.contact_person_name,
    formatAdminCategories(admin),
    admin?.approval_status,
    admin?.lifecycle_status,
    admin?.registration_payment_status,
    admin?.city,
    admin?.state,
    admin?.pincode,
    ...getRegistrationServiceLines(admin).flatMap((line) => [line.key, line.label]),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

const matchesText = (value, query) =>
  !String(query || '').trim() ||
  String(value ?? '')
    .toLowerCase()
    .includes(String(query).trim().toLowerCase());

const stickyActionsHeaderSx = {
  position: 'sticky',
  right: 0,
  zIndex: 3,
  backgroundColor: '#fff',
  boxShadow: '-8px 0 8px -6px rgba(15, 23, 42, 0.18)',
  minWidth: 200,
  whiteSpace: 'nowrap',
};

const stickyActionsCellSx = {
  position: 'sticky',
  right: 0,
  zIndex: 1,
  backgroundColor: '#fff',
  boxShadow: '-8px 0 8px -6px rgba(15, 23, 42, 0.18)',
  minWidth: 200,
  verticalAlign: 'middle',
};

const ActionIconButton = ({ title, color = 'default', onClick, disabled, children }) => (
  <Tooltip title={title}>
    <span>
      <IconButton size="small" color={color} onClick={onClick} disabled={disabled}>
        {children}
      </IconButton>
    </span>
  </Tooltip>
);

const ColumnFilter = ({ label, value, onChange, width = 112 }) => (
  <Stack spacing={0.75} sx={{ minWidth: width }}>
    <Typography variant="caption" fontWeight={700}>
      {label}
    </Typography>
    <TextField
      value={value || ''}
      onChange={(event) => onChange(event.target.value)}
      size="small"
      placeholder={`Filter ${label.toLowerCase()}`}
      inputProps={{ 'aria-label': `Filter ${label}` }}
      sx={{
        '& .MuiInputBase-input': {
          py: 0.65,
          px: 1,
          fontSize: '0.72rem',
        },
      }}
    />
  </Stack>
);

const FranchiseeAdminApproval = () => {
  const { user } = useAuth();
  const canRegisterVyapar = ['app_admin', 'super_admin'].includes(user?.role) ||
    (user?.role === 'app_operator' && user?.permissions?.includes('vyapar_assisted_registration'));
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [allAdmins, setAllAdmins] = useState([]);
  const [availableFeatures, setAvailableFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [syncMyTownLoading, setSyncMyTownLoading] = useState(false);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const [featurePricing, setFeaturePricing] = useState({});
  const [saasPriceByKey, setSaasPriceByKey] = useState({});
  const [actionType, setActionType] = useState('approve'); // 'approve' or 'reject'
  const [rejectedReason, setRejectedReason] = useState('');
  const [shareAccessOnWhatsApp, setShareAccessOnWhatsApp] = useState(true);
  const [featureSearch, setFeatureSearch] = useState('');
  const [featuresRefreshing, setFeaturesRefreshing] = useState(false);
  const [pageTab, setPageTab] = useState('verification');
  const [adminTab, setAdminTab] = useState('active');
  const [lifecycleBusyId, setLifecycleBusyId] = useState('');
  const [usageStatsByAdmin, setUsageStatsByAdmin] = useState({});
  const [usageStatsLoading, setUsageStatsLoading] = useState(false);
  const [usageStatsDialogOpen, setUsageStatsDialogOpen] = useState(false);
  const [usageStatsDialogAdmin, setUsageStatsDialogAdmin] = useState(null);
  const [usageStatsDialogPeriod, setUsageStatsDialogPeriod] = useState('month');
  const [globalSearch, setGlobalSearch] = useState('');
  const [columnFilters, setColumnFilters] = useState({});
  const [statusQuickFilter, setStatusQuickFilter] = useState('all');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editAdmin, setEditAdmin] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [verificationFilter, setVerificationFilter] = useState('');
  const [verificationListingFilter, setVerificationListingFilter] = useState('');
  const [adminsFilter, setAdminsFilter] = useState('');
  const whatsappWindowRef = useRef(null);
  const isAppOperator =
    String(user?.role || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '_') === 'app_operator';

  useEffect(() => {
    loadData();
  }, []);

  const handleSyncMyTown = async () => {
    setSyncMyTownLoading(true);
    setError('');
    setSuccess('');
    try {
      const response = await axios.post(`${API_BASE_URL}/franchisee/admin/sync-my-town`);
      if (response.data.success) {
        setSuccess(response.data.message || 'Synced businesses to Citizen My Town');
      } else {
        setError(response.data.message || 'My Town sync failed');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'My Town sync failed');
      console.error('Sync My Town error:', err);
    } finally {
      setSyncMyTownLoading(false);
    }
  };

  const loadUsageStats = async () => {
    setUsageStatsLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/franchisee/admin/usage-stats`);
      if (response.data?.success) {
        setUsageStatsByAdmin(response.data.data?.byAdminId || {});
      }
    } catch (err) {
      console.warn('Usage stats unavailable:', err.response?.data?.message || err.message);
    } finally {
      setUsageStatsLoading(false);
    }
  };

  const loadData = async () => {
    setLoading(true);
    setError('');

    try {
      const [queueResponse, allAdminsResponse, featuresResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/franchisee/admin/verification-queue`),
        axios.get(`${API_BASE_URL}/franchisee/admin/all`),
        axios.get(`${API_BASE_URL}/franchisee/admin/available-features`),
      ]);

      if (queueResponse.data.success) {
        setPendingApprovals(queueResponse.data.data || []);
        const promoted = Number(queueResponse.data.auto_promoted?.promoted || 0);
        if (promoted > 0) {
          setSuccess(
            `${promoted} older pending registration(s) were auto-activated and added to the verification queue.`,
          );
        }
      }

      if (allAdminsResponse.data.success) {
        setAllAdmins(allAdminsResponse.data.data || []);
      }

      if (featuresResponse.data.success) {
        setAvailableFeatures(featuresResponse.data.data || []);
      }

      loadUsageStats();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load data');
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenUsageStats = (admin) => {
    setUsageStatsDialogAdmin(admin);
    setUsageStatsDialogPeriod('month');
    setUsageStatsDialogOpen(true);
  };

  const handleCloseUsageStats = () => {
    setUsageStatsDialogOpen(false);
    setUsageStatsDialogAdmin(null);
  };

  const setColumnFilter = (scope, column, value) => {
    setColumnFilters((current) => ({
      ...current,
      [`${scope}.${column}`]: value,
    }));
  };

  const getColumnFilter = (scope, column) => columnFilters[`${scope}.${column}`] || '';

  const handleOpenEditDialog = (admin) => {
    setEditAdmin(admin);
    setEditForm({
      phone: admin.phone || '',
      email: admin.email || '',
      business_name: admin.business_name || '',
      contact_person_name: admin.contact_person_name || '',
      address: admin.address || '',
      city: admin.city || '',
      state: admin.state || '',
      pincode: admin.pincode || '',
    });
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    if (editSaving) return;
    setEditDialogOpen(false);
    setEditAdmin(null);
    setEditForm({});
  };

  const handleSaveAdminDetails = async () => {
    if (!editAdmin) return;
    setEditSaving(true);
    setError('');
    try {
      const response = await axios.patch(
        `${API_BASE_URL}/franchisee/admin/${editAdmin.id}`,
        editForm,
      );
      if (!response.data?.success) {
        throw new Error(response.data?.message || 'Update failed');
      }
      setSuccess(response.data.message || 'Business admin details updated');
      setEditDialogOpen(false);
      setEditAdmin(null);
      setEditForm({});
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Update failed');
    } finally {
      setEditSaving(false);
    }
  };

  const handleOpenApprovalDialog = async (admin, action) => {
    setSelectedAdmin(admin);
    setActionType(action);
    setShareAccessOnWhatsApp(action === 'approve' && admin?.approval_status !== 'approved');

    let catalogByKey = saasPriceByKey;
    try {
      const pricingRes = await axios.get(`${API_BASE_URL}/franchisee/admin/saas-pricing`);
      const rows = pricingRes.data?.data?.features || [];
      catalogByKey = {};
      rows.forEach((row) => {
        if (row?.feature_key) catalogByKey[row.feature_key] = row;
      });
      setSaasPriceByKey(catalogByKey);
    } catch (err) {
      console.warn('Could not load SaaS catalog prices for review dialog', err);
    }

    if (action === 'approve') {
      // Seed from DB features (queue + all-admins), then requested, then type template.
      // Verification-queue rows used to omit `features`, so Save with empty selection wiped grants.
      let existingFeatures = resolveEnabledFeatureKeys(admin, allAdmins);
      // Billing modules always include View Bills in the grant set (can still set fee free/custom).
      if (
        (existingFeatures.includes('smartbill') ||
          existingFeatures.includes('cart_model_bill')) &&
        !existingFeatures.includes('view_bills')
      ) {
        existingFeatures = [...existingFeatures, 'view_bills'];
      }
      setSelectedFeatures(existingFeatures);
      setFeaturePricing(
        buildInitialFeaturePricing(admin, existingFeatures, catalogByKey),
      );
    } else {
      setSelectedFeatures([]);
      setFeaturePricing({});
    }

    setApprovalDialogOpen(true);
  };

  const handleCloseApprovalDialog = () => {
    setApprovalDialogOpen(false);
    setSelectedAdmin(null);
    setSelectedFeatures([]);
    setFeaturePricing({});
    setRejectedReason('');
    setShareAccessOnWhatsApp(true);
    setFeatureSearch('');
  };

  const handleFeatureToggle = (feature) => {
    setSelectedFeatures((prev) => {
      let next;
      if (prev.includes(feature)) {
        next = prev.filter((f) => f !== feature);
      } else {
        next = [...prev, feature];
        if (
          (feature === 'smartbill' || feature === 'cart_model_bill') &&
          !next.includes('view_bills')
        ) {
          next = [...next, 'view_bills'];
        }
      }
      setFeaturePricing((prevPricing) => {
        const nextPricing = { ...prevPricing };
        Object.keys(nextPricing).forEach((key) => {
          if (!next.includes(key)) delete nextPricing[key];
        });
        next.forEach((key) => {
          if (nextPricing[key]) return;
          const hasBilling =
            next.includes('smartbill') || next.includes('cart_model_bill');
          if (
            NON_BILLABLE_FEATURE_KEYS.has(key) ||
            (key === 'view_bills' && hasBilling)
          ) {
            nextPricing[key] = { mode: 'free', amountRupees: '' };
          } else {
            nextPricing[key] = { mode: 'default', amountRupees: '' };
          }
        });
        // Keep View Bills free by default when billing is on and still on default.
        if (
          (next.includes('smartbill') || next.includes('cart_model_bill')) &&
          nextPricing.view_bills &&
          nextPricing.view_bills.mode === 'default'
        ) {
          nextPricing.view_bills = { mode: 'free', amountRupees: '' };
        }
        return nextPricing;
      });
      return next;
    });
  };

  const handleFeaturePricingModeChange = (featureKey, mode) => {
    setFeaturePricing((prev) => ({
      ...prev,
      [featureKey]: {
        mode,
        amountRupees:
          mode === 'custom'
            ? prev[featureKey]?.amountRupees ||
              String(
                (
                  (Number(saasPriceByKey[featureKey]?.amount_paise) || 0) / 100
                ).toFixed(2),
              )
            : '',
      },
    }));
  };

  const handleFeatureCustomAmountChange = (featureKey, amountRupees) => {
    setFeaturePricing((prev) => ({
      ...prev,
      [featureKey]: {
        mode: 'custom',
        amountRupees,
      },
    }));
  };

  const handleApproveReject = async () => {
    if (!selectedAdmin) return;

    const temporaryPin =
      actionType === 'approve' && shareAccessOnWhatsApp
        ? generateTemporaryPin()
        : null;
    const loginUrl = temporaryPin
      ? buildVyaparAutoLoginUrl(selectedAdmin.phone, temporaryPin)
      : '';
    const whatsappMessage = temporaryPin
      ? buildAccessMessage({
          admin: selectedAdmin,
          pin: temporaryPin,
          loginUrl,
        })
      : '';
    const whatsappUrl = temporaryPin
      ? buildWhatsAppUrl(selectedAdmin.phone, whatsappMessage)
      : '';

    if (whatsappUrl) {
      whatsappWindowRef.current = window.open('', '_blank');
    }

    try {
      const response = await axios.post(
        `${API_BASE_URL}/franchisee/admin/${selectedAdmin.id}/approve`,
        {
          action: actionType,
          features: actionType === 'approve' ? selectedFeatures : [],
          feature_pricing:
            actionType === 'approve' ? toApiFeaturePricing(featurePricing) : undefined,
          update_registration_pricing: actionType === 'approve',
          rejected_reason: actionType === 'reject' ? rejectedReason : null,
          share_whatsapp: actionType === 'approve' ? shareAccessOnWhatsApp : false,
          temporary_pin: temporaryPin,
        },
      );

      if (response.data.success) {
        const myTown = response.data.my_town;
        const pricingInfo = response.data.registration_pricing;
        if (actionType === 'approve') {
          if (selectedAdmin?.approval_status === 'approved') {
            const feeNote =
              pricingInfo?.skipped && pricingInfo?.reason === 'already_paid'
                ? ' Registration fee unchanged (already paid).'
                : pricingInfo && !pricingInfo.skipped
                  ? ` Registration total set to ${formatPaise(pricingInfo.total)}.`
                  : '';
            setSuccess(
              `Features saved. Mitra Bills cards will show after the business admin refreshes or re-logins.${feeNote}`,
            );
          } else if (myTown?.published) {
            setSuccess('Approved and published to Citizen My Town (All / categories / Recently Published).');
          } else if (myTown?.skipped) {
            setError(
              `Approved for dashboard, but not published to My Town (${myTown.reason || 'requirements missing'}). Use "Publish all to My Town".`,
            );
          } else {
            setSuccess('Approved successfully.');
          }
        }

        if (whatsappUrl && whatsappWindowRef.current) {
          whatsappWindowRef.current.location.href = whatsappUrl;
          whatsappWindowRef.current.focus();
        } else if (whatsappUrl) {
          window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
        }

        handleCloseApprovalDialog();
        loadData(); // Reload data
      } else {
        if (whatsappWindowRef.current) {
          whatsappWindowRef.current.close();
          whatsappWindowRef.current = null;
        }
        setError(response.data.message || 'Operation failed');
      }
    } catch (err) {
      if (whatsappWindowRef.current) {
        whatsappWindowRef.current.close();
        whatsappWindowRef.current = null;
      }
      setError(err.response?.data?.message || 'Operation failed');
      console.error('Error approving/rejecting:', err);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'rejected':
        return 'error';
      case 'pending':
        return 'warning';
      case 'suspended':
        return 'default';
      case 'flagged':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <CheckCircleIcon />;
      case 'rejected':
        return <CancelIcon />;
      case 'pending':
        return <PendingIcon />;
      case 'suspended':
        return <BlockIcon />;
      case 'flagged':
        return <FlagIcon />;
      default:
        return null;
    }
  };

  const handleLifecycleAction = async (admin, nextStatus) => {
    const businessName = resolveBusinessName(admin);
    const actionLabel =
      nextStatus === 'approved'
        ? 'reactivate'
        : nextStatus === 'suspended'
          ? 'suspend'
          : 'move to the recycle bin';
    const reason = window.prompt(
      `Reason to ${actionLabel} ${businessName}?${nextStatus === 'flagged' ? '\n\nThis blocks login without deleting data. You can restore the account or permanently delete it from the Recycle Bin tab.' : ''}`,
      `${actionLabel} by app admin`,
    );

    if (reason === null) return;
    const sure = window.confirm(
      `Confirm ${actionLabel.toUpperCase()} for ${businessName} (${admin.phone})?`,
    );
    if (!sure) return;

    setLifecycleBusyId(`${admin.id}:${nextStatus}`);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/franchisee/admin/${admin.id}/status`,
        { status: nextStatus, reason },
      );
      if (!response.data?.success) {
        throw new Error(response.data?.message || 'Status update failed');
      }
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Status update failed');
      console.error('Business admin lifecycle action failed:', err);
    } finally {
      setLifecycleBusyId('');
    }
  };

  const handleMyTownListToggle = async (admin, list) => {
    const businessName = resolveBusinessName(admin);
    const sure = window.confirm(
      list
        ? `List ${businessName} in Citizen My Town categories?`
        : `Unlist ${businessName} from Citizen My Town categories?\n\nMitra login/trial state is not changed by this action.`,
    );
    if (!sure) return;

    setLifecycleBusyId(`${admin.id}:${list ? 'list' : 'unlist'}`);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/franchisee/admin/${admin.id}/${
          list ? 'publish-my-town' : 'unpublish-my-town'
        }`,
      );
      if (!response.data?.success) {
        throw new Error(response.data?.message || 'Citizen listing update failed');
      }
      await loadData();
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || 'Citizen listing update failed',
      );
      console.error('Citizen list/unlist failed:', err);
    } finally {
      setLifecycleBusyId('');
    }
  };

  const handleVerificationToggle = async (admin, verified) => {
    const businessName = resolveBusinessName(admin);
    const sure = window.confirm(
      verified
        ? `Mark ${businessName} as Verified in Citizen apps?`
        : `Mark ${businessName} as Not verified in Citizen apps?`,
    );
    if (!sure) return;

    setLifecycleBusyId(`${admin.id}:verify`);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/franchisee/admin/${admin.id}/verification`,
        { is_verified: verified },
      );
      if (!response.data?.success) {
        throw new Error(response.data?.message || 'Verification update failed');
      }
      await loadData();
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || 'Verification update failed',
      );
      console.error('Verification toggle failed:', err);
    } finally {
      setLifecycleBusyId('');
    }
  };

  const handlePermanentDelete = async (admin) => {
    const businessName = resolveBusinessName(admin);
    if (getAdminDisplayStatus(admin) !== 'flagged') {
      setError('Permanent delete is allowed only after the admin is moved to the Recycle Bin.');
      return;
    }

    const firstConfirm = window.confirm(
      `Permanent delete ${businessName} (${admin.phone})?\n\nThis removes the Mitra login, feature grants, and operators. The phone number can then be used for a new Mitra registration. This cannot be undone.`,
    );
    if (!firstConfirm) return;

    const confirmText = window.prompt(
      'Final confirmation required. Type PERMANENT DELETE to continue.',
    );
    if (confirmText !== 'PERMANENT DELETE') {
      setError('Permanent delete cancelled. Confirmation text did not match.');
      return;
    }

    setLifecycleBusyId(`${admin.id}:delete`);
    try {
      const response = await axios.delete(
        `${API_BASE_URL}/franchisee/admin/${admin.id}/permanent`,
        { data: { confirm_text: confirmText } },
      );
      if (!response.data?.success) {
        throw new Error(response.data?.message || 'Permanent delete failed');
      }
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Permanent delete failed');
      console.error('Permanent delete failed:', err);
    } finally {
      setLifecycleBusyId('');
    }
  };

  const refreshAvailableFeatures = async () => {
    setFeaturesRefreshing(true);
    try {
      const featuresResponse = await axios.get(
        `${API_BASE_URL}/franchisee/admin/available-features`,
      );
      if (featuresResponse.data.success) {
        setAvailableFeatures(featuresResponse.data.data || []);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update available features');
      console.error('Error refreshing features:', err);
    } finally {
      setFeaturesRefreshing(false);
    }
  };

  const allFeatureRows = useMemo(
    () => getMergedFranchiseFeatureCatalog(availableFeatures),
    [availableFeatures],
  );

  const selectedBusinessTypeKey = normalizeBusinessTypeKey(selectedAdmin?.business_type);
  const relevantFeatures = BUSINESS_TYPE_RELEVANT_FEATURES[selectedBusinessTypeKey] || [];
  const requestedFeatureSet = new Set(parseRequestedFeatures(selectedAdmin?.requested_features));
  const filteredFeatureRows = useMemo(() => {
    const query = featureSearch.trim().toLowerCase();
    if (!query) return allFeatureRows;

    return allFeatureRows.filter((feature) =>
      [
        feature.key,
        feature.label,
        feature.description,
        ...(Array.isArray(feature.keywords) ? feature.keywords : []),
      ]
        .join(' ')
        .toLowerCase()
        .includes(query),
    );
  }, [allFeatureRows, featureSearch]);

  const registrationPricingLocked = useMemo(() => {
    const status = String(selectedAdmin?.registration_payment_status || '')
      .toLowerCase();
    return status === 'paid';
  }, [selectedAdmin]);

  const reviewPricingPreview = useMemo(() => {
    let subtotal = 0;
    const lines = selectedFeatures
      .filter((key) => !NON_BILLABLE_FEATURE_KEYS.has(key))
      .map((key) => {
      const amountPaise = resolvePreviewFeaturePaise(
        key,
        selectedFeatures,
        featurePricing,
        saasPriceByKey,
      );
      subtotal += amountPaise;
      return {
        key,
        label: getFeatureMeta(key).label,
        amountPaise,
        mode: featurePricing[key]?.mode || 'default',
        catalogPaise: Number(saasPriceByKey[key]?.amount_paise) || 0,
      };
    });
    const existingDiscount = Number(selectedAdmin?.registration_discount_paise) || 0;
    // Keep coupon discount only if it still fits under subtotal.
    const discount = Math.min(existingDiscount, subtotal);
    return {
      lines,
      subtotal,
      discount,
      total: Math.max(0, subtotal - discount),
    };
  }, [
    selectedFeatures,
    featurePricing,
    saasPriceByKey,
    selectedAdmin,
  ]);
  const adminCounts = useMemo(() => {
    const counts = { active: 0, suspended: 0, flagged: 0 };
    allAdmins.forEach((admin) => {
      const status = getAdminDisplayStatus(admin);
      if (status === 'suspended') counts.suspended += 1;
      else if (status === 'flagged') counts.flagged += 1;
      else counts.active += 1;
    });
    return counts;
  }, [allAdmins]);

  const visibleAdmins = useMemo(() => {
    const tab = ADMIN_TABS.find((item) => item.key === adminTab) || ADMIN_TABS[0];
    const query = (globalSearch || adminsFilter).trim().toLowerCase();
    return allAdmins.filter((admin) => {
      const displayStatus = getAdminDisplayStatus(admin);
      const quickFilterMatches =
        statusQuickFilter === 'all' ||
        (statusQuickFilter === 'verified' && Boolean(admin.is_verified)) ||
        (statusQuickFilter === 'not_verified' && !admin.is_verified) ||
        (statusQuickFilter === 'paid' &&
          String(admin.registration_payment_status).toLowerCase() === 'paid') ||
        (statusQuickFilter === 'paused' &&
          (displayStatus === 'suspended' || admin.mitra_access === 'restricted'));

      return (
        (statusQuickFilter === 'paused' || tab.statuses.includes(displayStatus)) &&
        quickFilterMatches &&
        (!query || searchableAdminText(admin).includes(query))
      );
    });
  }, [adminTab, adminsFilter, allAdmins, globalSearch, statusQuickFilter]);

  const registrationPaymentRows = useMemo(() => {
    return allAdmins
      .filter(hasRegistrationFee)
      .map((admin) => ({
        admin,
        lines: getRegistrationServiceLines(admin),
        paidAt: getRegistrationPaidAt(admin),
        totalPaise: Number(admin.registration_total_paise) || 0,
        payStatus: String(admin.registration_payment_status || '').toLowerCase(),
      }))
      .sort((a, b) => {
        if (a.payStatus === 'paid' && b.payStatus !== 'paid') return -1;
        if (b.payStatus === 'paid' && a.payStatus !== 'paid') return 1;
        const aTime = a.paidAt ? new Date(a.paidAt).getTime() : 0;
        const bTime = b.paidAt ? new Date(b.paidAt).getTime() : 0;
        return bTime - aTime;
      });
  }, [allAdmins]);

  const registrationPaymentsSummary = useMemo(() => {
    let totalCollectedPaise = 0;
    let paidCount = 0;
    let pendingCount = 0;
    let pendingAmountPaise = 0;

    registrationPaymentRows.forEach((row) => {
      if (row.payStatus === 'paid') {
        paidCount += 1;
        totalCollectedPaise += row.totalPaise;
      } else if (row.payStatus === 'unpaid' || row.payStatus === 'pending_checkout') {
        pendingCount += 1;
        pendingAmountPaise += row.totalPaise;
      }
    });

    return { totalCollectedPaise, paidCount, pendingCount, pendingAmountPaise };
  }, [registrationPaymentRows]);

  const filteredRegistrationPaymentRows = useMemo(() => {
    const query = globalSearch.trim().toLowerCase();
    return registrationPaymentRows.filter(({ admin, payStatus, totalPaise, paidAt }) => (
      (!query || searchableAdminText(admin).includes(query)) &&
      matchesText(resolveBusinessName(admin), getColumnFilter('payments', 'business')) &&
      matchesText(admin.phone, getColumnFilter('payments', 'phone')) &&
      matchesText(payStatus, getColumnFilter('payments', 'payment')) &&
      matchesText(formatPaise(totalPaise), getColumnFilter('payments', 'total')) &&
      matchesText(formatUsageDateTime(paidAt), getColumnFilter('payments', 'paidOn')) &&
      matchesText(
        formatUsageDateTime(admin.created_at),
        getColumnFilter('payments', 'registeredOn'),
      ) &&
      matchesText(
        getRegistrationServiceLines(admin).map((line) => line.label).join(' '),
        getColumnFilter('payments', 'services'),
      )
    ));
  }, [columnFilters, globalSearch, registrationPaymentRows]);

  const filteredPendingApprovals = useMemo(() => {
    const query = (globalSearch || verificationFilter).trim().toLowerCase();
    const listing = String(verificationListingFilter || '').trim().toLowerCase();
    return pendingApprovals.filter((admin) => {
      const listed = Boolean(admin.my_town_listed);
      const listingOk =
        !listing ||
        (listing === 'listed' && listed) ||
        (listing === 'unlisted' && !listed);
      const haystack = searchableAdminText(admin);
      const queryOk = !query || haystack.includes(query);
      return listingOk && queryOk;
    });
  }, [globalSearch, pendingApprovals, verificationFilter, verificationListingFilter]);

  const overviewStats = useMemo(() => ({
    total: allAdmins.length,
    verified: allAdmins.filter((admin) => Boolean(admin.is_verified)).length,
    notVerified: allAdmins.filter((admin) => !admin.is_verified).length,
    paid: allAdmins.filter(
      (admin) => String(admin.registration_payment_status || '').toLowerCase() === 'paid',
    ).length,
    paused: allAdmins.filter(
      (admin) =>
        getAdminDisplayStatus(admin) === 'suspended' || admin.mitra_access === 'restricted',
    ).length,
  }), [allAdmins]);

  return (
    <DashboardLayout>
      <Container maxWidth="xl" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
        <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
          <Box sx={{ minWidth: 0, flex: '1 1 280px' }}>
            {/* mitra users crud — AA login Mitra / franchisee admin directory */}
            <Typography variant="h4" fontWeight={700} gutterBottom sx={{ fontSize: { xs: '1.45rem', sm: '2rem' } }}>
              Mitra Users CRUD
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Business/Service Admin Verification — list, review, approve, suspend, and manage Mitra accounts.
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              Admin control of Mitra account and business data is for verification, fraud prevention,
              support, and platform safety only. A disclaimer does not waive privacy obligations —
              keep processing purpose-limited and document it in Terms / Privacy Policy (DPDP).
            </Alert>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 640 }}>
              New Mitra registrations go live automatically in Citizen My Town with a Not verified badge.
              Review profiles here and grant the Verified badge when ready.
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="primary"
            disabled={syncMyTownLoading || loading}
            onClick={handleSyncMyTown}
            startIcon={syncMyTownLoading ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />}
            sx={{ whiteSpace: 'nowrap', alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
          >
            {syncMyTownLoading ? 'Publishing…' : 'Publish all to My Town'}
          </Button>
        </Box>

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              sx={{ mb: 2 }}
            >
              <Button
                component={RouterLink}
                to="/locsy/admin/franchisee-saas-pricing"
                variant="outlined"
                size="small"
              >
                Feature prices & coupons
              </Button>
              <TextField
                value={globalSearch}
                onChange={(event) => setGlobalSearch(event.target.value)}
                size="small"
                fullWidth
                placeholder="Global search: phone, part of name, email, service, status..."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ ml: { sm: 'auto' }, maxWidth: { sm: 560 } }}
              />
            </Stack>

            <Card>
              <Tabs
                value={pageTab}
                onChange={(event, value) => {
                  setPageTab(value);
                  if (value === 'recycle-bin') {
                    setAdminTab('flagged');
                    setStatusQuickFilter('all');
                  }
                }}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ px: 2, pt: 1, borderBottom: '1px solid #e5e7eb' }}
              >
                <Tab
                  value="payments"
                  label={`Registration payments (${registrationPaymentRows.length})`}
                  icon={<PaymentsIcon />}
                  iconPosition="start"
                />
                <Tab
                  value="verification"
                  label={`Awaiting verification (${pendingApprovals.length})`}
                  icon={<VerifiedUserIcon />}
                  iconPosition="start"
                />
                <Tab
                  value="admins"
                  label={`Business/Service Admins (${allAdmins.length})`}
                />
                <Tab
                  value="recycle-bin"
                  label={`Recycle Bin (${adminCounts.flagged || 0})`}
                  icon={<DeleteForeverIcon />}
                  iconPosition="start"
                />
                {!isAppOperator && (
                  <Tab
                    value="testing-logins"
                    label="Testing Logins"
                  />
                )}
                {canRegisterVyapar && (
                  <Tab
                    value="register-vyapar"
                    label="Register Vyapar"
                    id="register-vyapar-tab"
                    aria-controls="register-vyapar-panel"
                  />
                )}
              </Tabs>

              {pageTab === 'payments' && (
              <CardContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  SaaS registration fees collected from franchisees — amount paid, date, and services opted at signup.
                </Typography>

                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={4}>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f0fdf4' }}>
                      <Typography variant="caption" color="text.secondary">
                        Total collected (paid)
                      </Typography>
                      <Typography variant="h5" fontWeight={700}>
                        {formatPaise(registrationPaymentsSummary.totalCollectedPaise)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {registrationPaymentsSummary.paidCount} franchisee
                        {registrationPaymentsSummary.paidCount === 1 ? '' : 's'}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fffbeb' }}>
                      <Typography variant="caption" color="text.secondary">
                        Pending payment
                      </Typography>
                      <Typography variant="h5" fontWeight={700}>
                        {formatPaise(registrationPaymentsSummary.pendingAmountPaise)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {registrationPaymentsSummary.pendingCount} awaiting payment
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        With registration fee
                      </Typography>
                      <Typography variant="h5" fontWeight={700}>
                        {registrationPaymentRows.length}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        All registrations with a fee or payment status
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>

                <TableContainer>
                  <Table size="small" sx={{ minWidth: 1120 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell><ColumnFilter label="Business" value={getColumnFilter('payments', 'business')} onChange={(value) => setColumnFilter('payments', 'business', value)} /></TableCell>
                        <TableCell><ColumnFilter label="Phone" value={getColumnFilter('payments', 'phone')} onChange={(value) => setColumnFilter('payments', 'phone', value)} /></TableCell>
                        <TableCell><ColumnFilter label="Payment" value={getColumnFilter('payments', 'payment')} onChange={(value) => setColumnFilter('payments', 'payment', value)} /></TableCell>
                        <TableCell><ColumnFilter label="Total" value={getColumnFilter('payments', 'total')} onChange={(value) => setColumnFilter('payments', 'total', value)} /></TableCell>
                        <TableCell><ColumnFilter label="Paid on" value={getColumnFilter('payments', 'paidOn')} onChange={(value) => setColumnFilter('payments', 'paidOn', value)} /></TableCell>
                        <TableCell><ColumnFilter label="Registered" value={getColumnFilter('payments', 'registeredOn')} onChange={(value) => setColumnFilter('payments', 'registeredOn', value)} /></TableCell>
                        <TableCell><ColumnFilter label="Services" value={getColumnFilter('payments', 'services')} onChange={(value) => setColumnFilter('payments', 'services', value)} width={180} /></TableCell>
                        <TableCell sx={stickyActionsHeaderSx}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredRegistrationPaymentRows.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} align="center">
                            {registrationPaymentRows.length
                              ? 'No registration payments match the current search and filters'
                              : 'No registration payments recorded yet'}
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredRegistrationPaymentRows.map(({ admin, payStatus, totalPaise, paidAt }) => (
                          <TableRow key={admin.id} hover>
                            <TableCell>{resolveBusinessName(admin)}</TableCell>
                            <TableCell>{admin.phone}</TableCell>
                            <TableCell>
                              <Chip
                                label={payStatus || '—'}
                                size="small"
                                color={registrationPaymentStatusColor(payStatus)}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" fontWeight={600}>
                                {formatPaise(totalPaise)}
                              </Typography>
                              {Number(admin.registration_discount_paise) > 0 && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  −{formatPaise(admin.registration_discount_paise)} discount
                                  {admin.registration_coupon_code
                                    ? ` (${admin.registration_coupon_code})`
                                    : ''}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>{formatUsageDateTime(paidAt)}</TableCell>
                            <TableCell>{formatUsageDateTime(admin.created_at)}</TableCell>
                            <TableCell sx={{ minWidth: 220 }}>
                              {renderRegistrationServices(admin)}
                            </TableCell>
                            <TableCell sx={stickyActionsCellSx}>
                              <Button
                                size="small"
                                startIcon={<EditIcon />}
                                onClick={() => handleOpenEditDialog(admin)}
                              >
                                Edit
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
              )}

              {pageTab === 'verification' && (
              <CardContent sx={{ px: { xs: 1.5, sm: 2.5 }, py: { xs: 2, sm: 2.5 } }}>
                <Alert severity="info" sx={{ mb: 2.5, borderRadius: 2 }}>
                  These Mitra accounts are already live for citizens (searchable in My Town) with a
                  Not verified badge. Review profile details, then grant Verified.
                </Alert>

                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1.5}
                  sx={{ mb: 2.5 }}
                  alignItems={{ xs: 'stretch', sm: 'center' }}
                >
                  <TextField
                    size="small"
                    fullWidth
                    label="Filter queue"
                    placeholder="Phone, business, email, type…"
                    value={verificationFilter}
                    onChange={(event) => setVerificationFilter(event.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ maxWidth: { sm: 360 } }}
                  />
                  <TextField
                    select
                    size="small"
                    label="Citizen listing"
                    value={verificationListingFilter}
                    onChange={(event) => setVerificationListingFilter(event.target.value)}
                    sx={{ minWidth: { xs: '100%', sm: 180 } }}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="listed">Listed</MenuItem>
                    <MenuItem value="unlisted">Unlisted</MenuItem>
                  </TextField>
                  <Box sx={{ flex: 1 }} />
                  <Typography variant="body2" color="text.secondary" sx={{ alignSelf: { sm: 'center' } }}>
                    {filteredPendingApprovals.length} of {pendingApprovals.length} awaiting verification
                  </Typography>
                </Stack>

                {filteredPendingApprovals.length === 0 ? (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 4,
                      textAlign: 'center',
                      borderRadius: 3,
                      bgcolor: '#f8fafc',
                    }}
                  >
                    <VerifiedUserIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                    <Typography fontWeight={700} gutterBottom>
                      {pendingApprovals.length
                        ? 'No businesses match your filters'
                        : 'Verification queue is empty'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      New registrations appear here automatically after signup.
                    </Typography>
                  </Paper>
                ) : (
                  <Grid container spacing={2}>
                    {filteredPendingApprovals.map((admin) => {
                      const payStatus = String(
                        admin.registration_payment_status || '',
                      ).toLowerCase();
                      const listed = Boolean(admin.my_town_listed);
                      const serviceCount = getRegistrationServiceLines(admin).length;
                      const verifying = lifecycleBusyId === `${admin.id}:verify`;

                      return (
                        <Grid item xs={12} md={6} xl={4} key={admin.id}>
                          <Paper
                            variant="outlined"
                            sx={{
                              height: '100%',
                              borderRadius: 3,
                              p: 2,
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 1.25,
                              borderColor: '#e2e8f0',
                              transition: 'box-shadow 0.2s, border-color 0.2s',
                              '&:hover': {
                                borderColor: '#93c5fd',
                                boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)',
                              },
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={1}
                              justifyContent="space-between"
                              alignItems="flex-start"
                            >
                              <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Stack direction="row" spacing={1.25} alignItems="center">
                                  <Box
                                    sx={{
                                      width: 44,
                                      height: 44,
                                      borderRadius: 2,
                                      overflow: 'hidden',
                                      flexShrink: 0,
                                      bgcolor: '#e2e8f0',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      border: '1px solid #cbd5e1',
                                    }}
                                  >
                                    {admin.profile_image_url ? (
                                      <Box
                                        component="img"
                                        src={
                                          String(admin.profile_image_url).startsWith('http')
                                            ? admin.profile_image_url
                                            : `${API_BASE_URL.replace(/\/api$/, '')}${
                                                String(admin.profile_image_url).startsWith('/')
                                                  ? ''
                                                  : '/'
                                              }${admin.profile_image_url}`
                                        }
                                        alt=""
                                        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                      />
                                    ) : (
                                      <GppBadIcon fontSize="small" color="disabled" />
                                    )}
                                  </Box>
                                  <Box sx={{ minWidth: 0 }}>
                                    <Typography
                                      variant="subtitle1"
                                      fontWeight={800}
                                      sx={{
                                        lineHeight: 1.3,
                                        wordBreak: 'break-word',
                                      }}
                                    >
                                      {resolveBusinessName(admin)}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{ mt: 0.35 }}
                                    >
                                      {admin.phone || '—'}
                                      {admin.email ? ` · ${admin.email}` : ''}
                                    </Typography>
                                  </Box>
                                </Stack>
                              </Box>
                              <Stack direction="row" spacing={0.75} flexShrink={0} flexWrap="wrap" useFlexGap>
                                <Chip
                                  size="small"
                                  icon={<GppBadIcon />}
                                  label="Not verified"
                                  variant="outlined"
                                />
                                <Chip
                                  size="small"
                                  icon={listed ? <PublicIcon /> : <PublicOffIcon />}
                                  label={listed ? 'Listed' : 'Unlisted'}
                                  color={listed ? 'info' : 'default'}
                                  variant="outlined"
                                />
                              </Stack>
                            </Stack>

                            <Typography variant="body2" color="text.secondary">
                              {formatAdminCategories(admin)}
                            </Typography>

                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                              <Chip
                                label={payStatus || '—'}
                                size="small"
                                color={registrationPaymentStatusColor(payStatus)}
                              />
                              {hasRegistrationFee(admin) && (
                                <Typography variant="body2" fontWeight={700}>
                                  {formatPaise(admin.registration_total_paise)}
                                </Typography>
                              )}
                              <Typography variant="caption" color="text.secondary">
                                Registered {new Date(admin.created_at).toLocaleDateString('en-IN', {
                                  day: 'numeric',
                                  month: 'short',
                                  year: 'numeric',
                                })}
                              </Typography>
                              {serviceCount > 0 && (
                                <Typography variant="caption" color="text.secondary">
                                  · {serviceCount} service{serviceCount === 1 ? '' : 's'}
                                </Typography>
                              )}
                            </Stack>

                            <Box sx={{ mt: 0.5 }}>
                              {renderRegistrationServices(admin, { maxChips: 2 })}
                            </Box>

                            <Stack
                              direction={{ xs: 'column', sm: 'row' }}
                              spacing={1}
                              sx={{ mt: 'auto', pt: 1.5 }}
                            >
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<EditIcon />}
                                onClick={() => handleOpenEditDialog(admin)}
                                sx={{ flex: { sm: 1 }, whiteSpace: 'nowrap' }}
                              >
                                Edit
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<VisibilityIcon />}
                                onClick={() => handleOpenApprovalDialog(admin, 'approve')}
                                sx={{ flex: { sm: 1 }, whiteSpace: 'nowrap' }}
                              >
                                Review
                              </Button>
                              <Button
                                size="small"
                                variant="contained"
                                color="success"
                                startIcon={
                                  verifying ? (
                                    <CircularProgress size={14} color="inherit" />
                                  ) : (
                                    <VerifiedUserIcon />
                                  )
                                }
                                disabled={verifying}
                                onClick={() => handleVerificationToggle(admin, true)}
                                sx={{ flex: { sm: 1.2 }, whiteSpace: 'nowrap', fontWeight: 700 }}
                              >
                                Verify
                              </Button>
                            </Stack>
                          </Paper>
                        </Grid>
                      );
                    })}
                  </Grid>
                )}
              </CardContent>
              )}

              {(pageTab === 'admins' || pageTab === 'recycle-bin') && (
              <CardContent sx={{ px: { xs: 1.5, sm: 2.5 }, py: { xs: 2, sm: 2.5 } }}>
                <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                  New registrations auto-publish as Not verified with a 1-month Mitra trial.
                  Use Verify / Not verified for the Citizen badge, and List / Unlist for My Town.
                  Pause blocks login. Recycle Bin is soft delete: accounts can be restored or
                  permanently deleted to reuse their phone number.
                </Alert>

                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1.5}
                  alignItems={{ xs: 'stretch', sm: 'center' }}
                  sx={{ mb: 2 }}
                >
                  <TextField
                    size="small"
                    fullWidth
                    label="Filter admins"
                    placeholder="Phone, business, email…"
                    value={adminsFilter}
                    onChange={(event) => setAdminsFilter(event.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ maxWidth: { sm: 360 } }}
                  />
                  <Box sx={{ flex: 1 }} />
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={loadData}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    Refresh
                  </Button>
                </Stack>

                <Tabs
                  value={adminTab}
                  onChange={(event, value) => {
                    setPageTab('admins');
                    setAdminTab(value);
                    setStatusQuickFilter('all');
                  }}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{ mb: 2, borderBottom: '1px solid #e5e7eb' }}
                >
                  {ADMIN_TABS.map((tab) => (
                    <Tab
                      key={tab.key}
                      value={tab.key}
                      label={`${tab.label} (${adminCounts[tab.key] || 0})`}
                    />
                  ))}
                </Tabs>

                {visibleAdmins.length === 0 ? (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 4,
                      textAlign: 'center',
                      borderRadius: 3,
                      bgcolor: '#f8fafc',
                    }}
                  >
                    <Typography fontWeight={700} gutterBottom>
                      No {adminTab} businesses found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Try another tab or clear your search filter.
                    </Typography>
                  </Paper>
                ) : (
                  <Grid container spacing={2}>
                    {visibleAdmins.map((admin) => {
                      const enabledFeatures =
                        admin.features
                          ?.filter((f) => f.is_enabled)
                          .map((f) => f.feature_name) || [];
                      const requested = parseRequestedFeatures(admin.requested_features);
                      const featurePreview =
                        enabledFeatures.length > 0
                          ? enabledFeatures
                          : requested.map((f) => `Requested: ${f}`);
                      const isBusy = lifecycleBusyId.startsWith(`${admin.id}:`);
                      const displayStatus = getAdminDisplayStatus(admin);
                      const usageStats = usageStatsByAdmin[admin.id];
                      const activityMeta =
                        ACTIVITY_TIER_META[usageStats?.activityTier] ||
                        ACTIVITY_TIER_META.never;
                      const isVerified = Boolean(admin.is_verified);
                      const isListed = Boolean(admin.my_town_listed);
                      const mitraAccess = String(admin.mitra_access || 'full');

                      return (
                        <Grid item xs={12} md={6} xl={4} key={admin.id}>
                          <Paper
                            variant="outlined"
                            sx={{
                              height: '100%',
                              borderRadius: 3,
                              p: 2,
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 1.25,
                              borderColor: '#e2e8f0',
                              '&:hover': {
                                borderColor: '#93c5fd',
                                boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)',
                              },
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={1}
                              justifyContent="space-between"
                              alignItems="flex-start"
                            >
                              <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography
                                  variant="subtitle1"
                                  fontWeight={800}
                                  sx={{ lineHeight: 1.3, wordBreak: 'break-word' }}
                                >
                                  {resolveBusinessName(admin)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                                  {admin.phone || '—'}
                                  {admin.email ? ` · ${admin.email}` : ''}
                                </Typography>
                              </Box>
                              <Chip
                                icon={getStatusIcon(displayStatus)}
                                label={displayStatus}
                                color={getStatusColor(displayStatus)}
                                size="small"
                              />
                            </Stack>

                            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                              <Chip
                                size="small"
                                icon={isVerified ? <VerifiedUserIcon /> : <GppBadIcon />}
                                label={isVerified ? 'Verified' : 'Not verified'}
                                color={isVerified ? 'success' : 'default'}
                                variant={isVerified ? 'filled' : 'outlined'}
                              />
                              <Chip
                                size="small"
                                icon={isListed ? <PublicIcon /> : <PublicOffIcon />}
                                label={isListed ? 'Listed' : 'Unlisted'}
                                color={isListed ? 'info' : 'default'}
                                variant="outlined"
                              />
                              {mitraAccess === 'restricted' && (
                                <Chip
                                  size="small"
                                  label="Trial ended"
                                  color="warning"
                                  variant="outlined"
                                />
                              )}
                            </Stack>

                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                              {usageStatsLoading && !usageStats ? (
                                <CircularProgress size={18} />
                              ) : (
                                <Chip
                                  icon={<InsightsIcon />}
                                  label={
                                    usageStats
                                      ? buildUsageChipLabel(usageStats, 'month')
                                      : 'No usage data'
                                  }
                                  size="small"
                                  color={activityMeta.color}
                                  variant={usageStats ? 'filled' : 'outlined'}
                                  onClick={() => handleOpenUsageStats(admin)}
                                  sx={{ cursor: 'pointer', maxWidth: '100%' }}
                                />
                              )}
                              <Typography variant="caption" color="text.secondary">
                                {featurePreview.length} feature
                                {featurePreview.length === 1 ? '' : 's'}
                                {admin.approved_by_name
                                  ? ` · by ${admin.approved_by_name}`
                                  : ''}
                              </Typography>
                            </Stack>

                            {featurePreview.length > 0 && (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {featurePreview.slice(0, 3).map((feature) => (
                                  <Chip
                                    key={feature}
                                    label={getFeatureMeta(feature.replace(/^Requested:\s*/, '')).label || feature}
                                    size="small"
                                    variant="outlined"
                                  />
                                ))}
                                {featurePreview.length > 3 && (
                                  <Chip
                                    label={`+${featurePreview.length - 3} more`}
                                    size="small"
                                    variant="outlined"
                                  />
                                )}
                              </Box>
                            )}

                            <Stack
                              direction="row"
                              spacing={0.5}
                              flexWrap="wrap"
                              useFlexGap
                              sx={{ mt: 'auto', pt: 1.25 }}
                            >
                              {displayStatus !== 'flagged' && (
                                <>
                                  <ActionIconButton
                                    title="Edit details"
                                    disabled={isBusy}
                                    onClick={() => handleOpenEditDialog(admin)}
                                  >
                                    <EditIcon fontSize="small" />
                                  </ActionIconButton>
                                  <ActionIconButton
                                    title="Review / features"
                                    disabled={isBusy}
                                    onClick={() => handleOpenApprovalDialog(admin, 'approve')}
                                  >
                                    <VisibilityIcon fontSize="small" />
                                  </ActionIconButton>
                                  <ActionIconButton
                                    title={isVerified ? 'Mark Not verified' : 'Verify'}
                                    color={isVerified ? 'default' : 'success'}
                                    disabled={isBusy}
                                    onClick={() => handleVerificationToggle(admin, !isVerified)}
                                  >
                                    {isVerified ? (
                                      <GppBadIcon fontSize="small" />
                                    ) : (
                                      <VerifiedUserIcon fontSize="small" />
                                    )}
                                  </ActionIconButton>
                                  <ActionIconButton
                                    title={isListed ? 'Unlist from My Town' : 'List in My Town'}
                                    color={isListed ? 'warning' : 'info'}
                                    disabled={isBusy}
                                    onClick={() => handleMyTownListToggle(admin, !isListed)}
                                  >
                                    {isListed ? (
                                      <PublicOffIcon fontSize="small" />
                                    ) : (
                                      <PublicIcon fontSize="small" />
                                    )}
                                  </ActionIconButton>
                                </>
                              )}
                              {displayStatus === 'approved' && (
                                <ActionIconButton
                                  title="Pause services"
                                  color="warning"
                                  disabled={isBusy}
                                  onClick={() => handleLifecycleAction(admin, 'suspended')}
                                >
                                  <BlockIcon fontSize="small" />
                                </ActionIconButton>
                              )}
                              {displayStatus === 'suspended' && (
                                <ActionIconButton
                                  title="Resume services"
                                  color="success"
                                  disabled={isBusy}
                                  onClick={() => handleLifecycleAction(admin, 'approved')}
                                >
                                  <RestoreIcon fontSize="small" />
                                </ActionIconButton>
                              )}
                              {displayStatus !== 'flagged' && (
                                <ActionIconButton
                                  title="Move to Recycle Bin"
                                  color="error"
                                  disabled={isBusy}
                                  onClick={() => handleLifecycleAction(admin, 'flagged')}
                                >
                                  <FlagIcon fontSize="small" />
                                </ActionIconButton>
                              )}
                              {displayStatus === 'flagged' && (
                                <>
                                  <ActionIconButton
                                    title="Resume services"
                                    color="success"
                                    disabled={isBusy}
                                    onClick={() => handleLifecycleAction(admin, 'approved')}
                                  >
                                    <RestoreIcon fontSize="small" />
                                  </ActionIconButton>
                                  <ActionIconButton
                                    title="Delete permanently and allow phone re-registration"
                                    color="error"
                                    disabled={isBusy}
                                    onClick={() => handlePermanentDelete(admin)}
                                  >
                                    <DeleteForeverIcon fontSize="small" />
                                  </ActionIconButton>
                                </>
                              )}
                            </Stack>
                          </Paper>
                        </Grid>
                      );
                    })}
                  </Grid>
                )}
              </CardContent>
              )}
              {pageTab === 'testing-logins' && !isAppOperator && (
                <TestingLoginsPanel />
              )}
              {pageTab === 'register-vyapar' && canRegisterVyapar && (
                <CardContent
                  role="tabpanel"
                  id="register-vyapar-panel"
                  aria-labelledby="register-vyapar-tab"
                >
                  <AssistedRegistrationForm />
                </CardContent>
              )}
            </Card>
            <Paper
              elevation={4}
              sx={{
                position: 'sticky',
                bottom: 8,
                zIndex: 5,
                mt: 2,
                p: 1.25,
                borderRadius: 2,
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
              >
                <Typography variant="caption" fontWeight={800} sx={{ px: 0.5 }}>
                  Business stats
                </Typography>
                {[
                  ['all', `Total ${overviewStats.total}`, 'default'],
                  ['verified', `Verified ${overviewStats.verified}`, 'success'],
                  ['not_verified', `Not verified ${overviewStats.notVerified}`, 'default'],
                  ['paid', `Paid ${overviewStats.paid}`, 'primary'],
                  ['paused', `Paused ${overviewStats.paused}`, 'warning'],
                ].map(([key, label, color]) => (
                  <Chip
                    key={key}
                    label={label}
                    color={color}
                    size="small"
                    variant={statusQuickFilter === key ? 'filled' : 'outlined'}
                    onClick={() => {
                      setStatusQuickFilter(key);
                      setPageTab('admins');
                      if (key !== 'paused') setAdminTab('active');
                    }}
                  />
                ))}
              </Stack>
            </Paper>
          </>
        )}

        <Dialog
          open={editDialogOpen}
          onClose={handleCloseEditDialog}
          maxWidth="md"
          fullWidth
          fullScreen={false}
          PaperProps={{ sx: { m: { xs: 1, sm: 3 }, width: { xs: 'calc(100% - 16px)', sm: '100%' } } }}
        >
          <DialogTitle>Edit business admin details</DialogTitle>
          <DialogContent dividers>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Changing the phone number changes the dashboard login ID. If this business is
              published, the linked Citizen listing is updated in the same transaction.
            </Alert>
            <Grid container spacing={2}>
              {[
                ['phone', 'Phone number', true],
                ['email', 'Email', true],
                ['business_name', 'Business name', true],
                ['contact_person_name', 'Contact person', true],
                ['address', 'Address', false],
                ['city', 'City', false],
                ['state', 'State', false],
                ['pincode', 'Pincode', false],
              ].map(([field, label, required]) => (
                <Grid item xs={12} sm={field === 'address' ? 12 : 6} key={field}>
                  <TextField
                    fullWidth
                    required={required}
                    label={label}
                    value={editForm[field] || ''}
                    onChange={(event) =>
                      setEditForm((current) => ({ ...current, [field]: event.target.value }))
                    }
                    inputProps={{
                      maxLength: field === 'phone' ? 15 : field === 'pincode' ? 6 : undefined,
                      inputMode:
                        field === 'phone' || field === 'pincode' ? 'numeric' : undefined,
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseEditDialog} disabled={editSaving}>Cancel</Button>
            <Button
              onClick={handleSaveAdminDetails}
              variant="contained"
              disabled={editSaving}
              startIcon={editSaving ? <CircularProgress size={16} /> : <EditIcon />}
            >
              {editSaving ? 'Saving…' : 'Save changes'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Approval Dialog */}
        <Dialog
          open={approvalDialogOpen}
          onClose={handleCloseApprovalDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {actionType === 'approve'
              ? 'Review Mitra profile & dashboard features'
              : 'Reject business/service admin'}
          </DialogTitle>
          <DialogContent>
            {selectedAdmin && (
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Phone:</strong> {selectedAdmin.phone}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Email:</strong> {selectedAdmin.email}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Business Name:</strong> {resolveBusinessName(selectedAdmin)}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Business Categories:</strong> {formatAdminCategories(selectedAdmin)}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Contact Person:</strong> {selectedAdmin.contact_person_name || '-'}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Address:</strong>{' '}
                  {[selectedAdmin.address, selectedAdmin.city, selectedAdmin.state, selectedAdmin.pincode]
                    .filter(Boolean)
                    .join(', ') || '—'}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Requested Plan:</strong> {selectedAdmin.requested_plan || 'postpaid_trial'}
                </Typography>

                <Box sx={{ mt: 2, mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>
                    Owner profile photo
                  </Typography>
                  {selectedAdmin.profile_image_url ? (
                    <Box
                      component="img"
                      src={
                        String(selectedAdmin.profile_image_url).startsWith('http')
                          ? selectedAdmin.profile_image_url
                          : `${API_BASE_URL.replace(/\/api$/, '')}${
                              String(selectedAdmin.profile_image_url).startsWith('/')
                                ? ''
                                : '/'
                            }${selectedAdmin.profile_image_url}`
                      }
                      alt="Owner profile"
                      sx={{
                        width: 96,
                        height: 96,
                        objectFit: 'cover',
                        borderRadius: 2,
                        border: '1px solid #e2e8f0',
                      }}
                    />
                  ) : (
                    <Alert severity="warning" sx={{ py: 0.5 }}>
                      Owner profile photo not uploaded yet.
                    </Alert>
                  )}
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>
                    Shop photos
                  </Typography>
                  {Array.isArray(selectedAdmin.shop_photo_urls) &&
                  selectedAdmin.shop_photo_urls.length > 0 ? (
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {selectedAdmin.shop_photo_urls.map((url) => {
                        const src = String(url || '').startsWith('http')
                          ? url
                          : `${API_BASE_URL.replace(/\/api$/, '')}${
                              String(url).startsWith('/') ? '' : '/'
                            }${url}`;
                        return (
                          <Box
                            key={src}
                            component="img"
                            src={src}
                            alt="Shop"
                            sx={{
                              width: 96,
                              height: 96,
                              objectFit: 'cover',
                              borderRadius: 2,
                              border: '1px solid #e2e8f0',
                            }}
                          />
                        );
                      })}
                    </Stack>
                  ) : (
                    <Alert severity="warning" sx={{ py: 0.5 }}>
                      Shop photos not uploaded yet.
                    </Alert>
                  )}
                </Box>

                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Registration payment:</strong>{' '}
                  <Chip
                    label={selectedAdmin.registration_payment_status || '—'}
                    size="small"
                    color={registrationPaymentStatusColor(
                      selectedAdmin.registration_payment_status,
                    )}
                    sx={{ ml: 0.5, verticalAlign: 'middle' }}
                  />
                </Typography>
                {parsePricingSnapshot(selectedAdmin)?.assisted_registration && (
                  <Grid item xs={12}>
                    <Alert severity="info">
                      Admin-assisted registration — phone OTP not verified at registration.
                      <br />Recorded by: {parsePricingSnapshot(selectedAdmin).assisted_registration.actor_id}
                      {' · '}{parsePricingSnapshot(selectedAdmin).assisted_registration.recorded_at}
                      <br />Authorization: {parsePricingSnapshot(selectedAdmin).assisted_registration.authorization_method}
                      {' · '}{parsePricingSnapshot(selectedAdmin).assisted_registration.authorization_reference}
                      <br />Notice: {parsePricingSnapshot(selectedAdmin).assisted_registration.notice_version}
                    </Alert>
                  </Grid>
                )}
                {hasRegistrationFee(selectedAdmin) && (
                  <Box sx={{ mt: 1, mb: 2 }}>
                    <Grid container spacing={1}>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="text.secondary">
                          Subtotal
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {formatPaise(selectedAdmin.registration_subtotal_paise)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="text.secondary">
                          Discount
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {formatPaise(selectedAdmin.registration_discount_paise)}
                          {selectedAdmin.registration_coupon_code
                            ? ` (${selectedAdmin.registration_coupon_code})`
                            : ''}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="text.secondary">
                          Total due
                        </Typography>
                        <Typography variant="body2" fontWeight={700}>
                          {formatPaise(selectedAdmin.registration_total_paise)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="text.secondary">
                          Paid on
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {formatUsageDateTime(getRegistrationPaidAt(selectedAdmin))}
                        </Typography>
                      </Grid>
                    </Grid>
                    {getRegistrationServiceLines(selectedAdmin).length > 0 && (
                      <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Service</TableCell>
                              <TableCell align="right">Price</TableCell>
                              <TableCell>Tenure</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {getRegistrationServiceLines(selectedAdmin).map((line) => (
                              <TableRow key={line.key}>
                                <TableCell>{line.label}</TableCell>
                                <TableCell align="right">
                                  {line.amountPaise != null
                                    ? formatPaise(line.amountPaise)
                                    : '—'}
                                </TableCell>
                                <TableCell>
                                  {line.tenureDays ? `${line.tenureDays} days` : '—'}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Box>
                )}
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Business Notes:</strong> {selectedAdmin.business_notes || '-'}
                </Typography>
                <Box sx={{ mt: 1, mb: 1 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Requested Features:</strong>
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {parseRequestedFeatures(selectedAdmin.requested_features).map((feature) => (
                      <Chip key={feature} label={feature} size="small" color="warning" variant="outlined" />
                    ))}
                    {parseRequestedFeatures(selectedAdmin.requested_features).length === 0 && (
                      <Typography variant="body2" color="text.secondary">No explicit feature request</Typography>
                    )}
                  </Box>
                </Box>

                {actionType === 'approve' ? (
                  <Box sx={{ mt: 3 }}>
                    <Stack
                      direction={{ xs: 'column', md: 'row' }}
                      justifyContent="space-between"
                      alignItems={{ xs: 'stretch', md: 'flex-start' }}
                      spacing={2}
                      sx={{ mb: 2 }}
                    >
                      <Box>
                        <Typography variant="h6" gutterBottom>
                          Select Features to Grant
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          All dashboard features are shown for every business type. Green means usually relevant to this business type; red means not usually related, but app admin can still grant it.
                        </Typography>
                      </Box>
                      <Button
                        variant="outlined"
                        startIcon={
                          featuresRefreshing ? (
                            <CircularProgress size={16} />
                          ) : (
                            <RefreshIcon />
                          )
                        }
                        disabled={featuresRefreshing}
                        onClick={refreshAvailableFeatures}
                        sx={{ whiteSpace: 'nowrap' }}
                      >
                        Update Features
                      </Button>
                    </Stack>
                    <TextField
                      fullWidth
                      size="small"
                      label="Search features"
                      placeholder="Search services, bills, LMS, cart model, coupons..."
                      value={featureSearch}
                      onChange={(event) => setFeatureSearch(event.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ mb: 2 }}
                    />
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                      <Chip size="small" color="success" label="Green: relevant" />
                      <Chip size="small" color="error" label="Red: non-relevant but grantable" />
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`${filteredFeatureRows.length}/${allFeatureRows.length} features shown`}
                      />
                      <Chip
                        size="small"
                        color="info"
                        variant="outlined"
                        label="Fee: Default catalog / Free / Custom per Mitra"
                      />
                    </Stack>
                    {registrationPricingLocked ? (
                      <Alert severity="info" sx={{ mb: 2 }}>
                        Registration fee already paid — feature access can still change, but fee
                        amounts stay locked for this Mitra.
                      </Alert>
                    ) : (
                      <Alert severity="success" sx={{ mb: 2 }}>
                        Default fee comes from{' '}
                        <strong>Feature prices &amp; coupons</strong>. For each granted service you
                        can set <strong>Default</strong>, <strong>Free</strong>, or a{' '}
                        <strong>Custom</strong> amount for this Mitra only. View Bills is free by
                        default with Smart Bill / Cart Model.
                      </Alert>
                    )}
                    <Grid container spacing={2}>
                      {filteredFeatureRows.map((feature) => {
                        const isSelected = selectedFeatures.includes(feature.key);
                        const isRelevant = relevantFeatures.includes(feature.key);
                        const wasRequested = requestedFeatureSet.has(feature.key);
                        const pricingEntry = featurePricing[feature.key] || {
                          mode: 'default',
                          amountRupees: '',
                        };
                        const catalogPaise =
                          Number(saasPriceByKey[feature.key]?.amount_paise) || 0;
                        const previewPaise = resolvePreviewFeaturePaise(
                          feature.key,
                          selectedFeatures,
                          featurePricing,
                          saasPriceByKey,
                        );
                        return (
                          <Grid item xs={12} sm={6} md={4} key={feature.key}>
                            <Paper
                              variant="outlined"
                              sx={{
                                height: '100%',
                                p: 1.25,
                                borderRadius: 2,
                                borderColor: isRelevant ? 'success.light' : 'error.light',
                                bgcolor: isRelevant ? '#f0fdf4' : '#fff1f2',
                                boxShadow: isSelected
                                  ? `0 0 0 2px ${isRelevant ? 'rgba(46, 125, 50, 0.22)' : 'rgba(211, 47, 47, 0.2)'}`
                                  : 'none',
                              }}
                            >
                              <FormControlLabel
                                sx={{ m: 0, alignItems: 'flex-start', width: '100%' }}
                                control={
                                  <Checkbox
                                    checked={isSelected}
                                    onChange={() => handleFeatureToggle(feature.key)}
                                    color={isRelevant ? 'success' : 'error'}
                                  />
                                }
                                label={
                                  <Box sx={{ width: '100%' }}>
                                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                                      <Typography variant="body2" fontWeight={800}>
                                        {feature.label}
                                      </Typography>
                                      <Chip
                                        label={isRelevant ? 'Relevant' : 'Non-relevant'}
                                        color={isRelevant ? 'success' : 'error'}
                                        size="small"
                                        variant={isRelevant ? 'filled' : 'outlined'}
                                      />
                                      {wasRequested && (
                                        <Chip
                                          label="Requested"
                                          color="warning"
                                          size="small"
                                          variant="outlined"
                                        />
                                      )}
                                      {catalogPaise > 0 && (
                                        <Chip
                                          label={`Default ${formatPaise(catalogPaise)}`}
                                          size="small"
                                          variant="outlined"
                                        />
                                      )}
                                    </Stack>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ display: 'block', mt: 0.5 }}
                                    >
                                      {feature.description}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: isSelected ? 'success.main' : 'error.main',
                                        display: 'block',
                                        mt: 0.5,
                                        fontWeight: 700,
                                      }}
                                    >
                                      {isSelected
                                        ? 'Access will be enabled'
                                        : 'Will be shown in red / not accessible'}
                                    </Typography>
                                    {feature.key === 'cart_model_bill' && (
                                      <Typography
                                        variant="caption"
                                        sx={{ color: 'primary.main', display: 'block', mt: 0.5 }}
                                      >
                                        Draft bills are included automatically. No separate draft permission.
                                      </Typography>
                                    )}
                                    {feature.key === 'view_bills' && (
                                      <Typography
                                        variant="caption"
                                        sx={{ color: 'primary.main', display: 'block', mt: 0.5 }}
                                      >
                                        Included free with Smart Bill / Cart Model unless you set a custom fee.
                                      </Typography>
                                    )}
                                    {feature.key === 'export_catalog' && (
                                      <Typography
                                        variant="caption"
                                        sx={{ color: 'primary.main', display: 'block', mt: 0.5 }}
                                      >
                                        Ops grant only — no registration fee. Enables Mitra Products → Share into
                                        catalog templates. Does not change membership plan or renewals.
                                      </Typography>
                                    )}
                                    {!isRelevant && (
                                    <Typography
                                      variant="caption"
                                      sx={{ color: 'error.dark', display: 'block', mt: 0.5 }}
                                    >
                                      Not related to registered business type, but app admin can grant.
                                    </Typography>
                                    )}
                                    {isSelected && !NON_BILLABLE_FEATURE_KEYS.has(feature.key) && (
                                      <Box
                                        sx={{ mt: 1.25, pt: 1, borderTop: '1px dashed #cbd5e1' }}
                                        onClick={(event) => event.stopPropagation()}
                                        onMouseDown={(event) => event.stopPropagation()}
                                      >
                                        <Typography
                                          variant="caption"
                                          fontWeight={700}
                                          sx={{ display: 'block', mb: 0.75 }}
                                        >
                                          Fee for this Mitra → {formatPaise(previewPaise)}
                                        </Typography>
                                        <TextField
                                          select
                                          size="small"
                                          fullWidth
                                          label="Pricing"
                                          disabled={registrationPricingLocked}
                                          value={pricingEntry.mode || 'default'}
                                          onChange={(event) =>
                                            handleFeaturePricingModeChange(
                                              feature.key,
                                              event.target.value,
                                            )
                                          }
                                          sx={{ mb: pricingEntry.mode === 'custom' ? 1 : 0 }}
                                        >
                                          <MenuItem value="default">
                                            Default catalog
                                            {catalogPaise > 0
                                              ? ` (${formatPaise(catalogPaise)})`
                                              : ' (₹0)'}
                                          </MenuItem>
                                          <MenuItem value="free">Free (₹0)</MenuItem>
                                          <MenuItem value="custom">Custom amount</MenuItem>
                                        </TextField>
                                        {pricingEntry.mode === 'custom' && (
                                          <TextField
                                            size="small"
                                            fullWidth
                                            type="number"
                                            label="Custom fee (₹)"
                                            disabled={registrationPricingLocked}
                                            value={pricingEntry.amountRupees}
                                            onChange={(event) =>
                                              handleFeatureCustomAmountChange(
                                                feature.key,
                                                event.target.value,
                                              )
                                            }
                                            inputProps={{ min: 0, step: '0.01' }}
                                          />
                                        )}
                                      </Box>
                                    )}
                                    {isSelected && feature.key === 'export_catalog' && (
                                      <Chip
                                        size="small"
                                        color="success"
                                        variant="outlined"
                                        label="No fee · access only"
                                        sx={{ mt: 1 }}
                                      />
                                    )}
                                  </Box>
                                }
                              />
                            </Paper>
                          </Grid>
                        );
                      })}
                    </Grid>
                    {filteredFeatureRows.length === 0 && (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        No features matched "{featureSearch}". Clear search or click Update Features.
                      </Alert>
                    )}

                    {!registrationPricingLocked && selectedFeatures.length > 0 && (
                      <Paper variant="outlined" sx={{ mt: 3, p: 2, bgcolor: '#f8fafc' }}>
                        <Typography variant="subtitle2" fontWeight={800} gutterBottom>
                          Registration fee preview (this Mitra)
                        </Typography>
                        <Stack spacing={0.5} sx={{ mb: 1.5 }}>
                          {reviewPricingPreview.lines.map((line) => (
                            <Stack
                              key={line.key}
                              direction="row"
                              justifyContent="space-between"
                              spacing={2}
                            >
                              <Typography variant="body2">
                                {line.label}
                                {line.mode !== 'default' ? ` · ${line.mode}` : ''}
                              </Typography>
                              <Typography variant="body2" fontWeight={600}>
                                {formatPaise(line.amountPaise)}
                              </Typography>
                            </Stack>
                          ))}
                        </Stack>
                        <Divider sx={{ my: 1 }} />
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="body2">Subtotal</Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {formatPaise(reviewPricingPreview.subtotal)}
                          </Typography>
                        </Stack>
                        {reviewPricingPreview.discount > 0 && (
                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="body2">Coupon discount</Typography>
                            <Typography variant="body2" fontWeight={600}>
                              −{formatPaise(reviewPricingPreview.discount)}
                            </Typography>
                          </Stack>
                        )}
                        <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
                          <Typography variant="subtitle2" fontWeight={800}>
                            Total due after Save
                          </Typography>
                          <Typography variant="subtitle2" fontWeight={800} color="primary.main">
                            {formatPaise(reviewPricingPreview.total)}
                          </Typography>
                        </Stack>
                      </Paper>
                    )}

                    <Box sx={{ mt: 3 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={shareAccessOnWhatsApp}
                            onChange={(event) => setShareAccessOnWhatsApp(event.target.checked)}
                            color="success"
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2">
                              Reset Vyapar PIN and share WhatsApp auto-login access
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Sends the Vyapar ID, a temporary PIN, and an auto-login link to the business admin.
                            </Typography>
                          </Box>
                        }
                      />
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" gutterBottom>
                      Rejection Reason (optional):
                    </Typography>
                    <textarea
                      value={rejectedReason}
                      onChange={(e) => setRejectedReason(e.target.value)}
                      style={{
                        width: '100%',
                        minHeight: '100px',
                        padding: '8px',
                        borderRadius: '4px',
                        border: '1px solid #ccc',
                      }}
                      placeholder="Enter reason for rejection..."
                    />
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseApprovalDialog}>Cancel</Button>
            <Button
              onClick={handleApproveReject}
              variant="contained"
              color={actionType === 'approve' ? 'success' : 'error'}
            >
              {actionType === 'approve'
                ? selectedAdmin?.approval_status === 'approved'
                  ? 'Save features & fees'
                  : 'Approve'
                : 'Reject'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={usageStatsDialogOpen}
          onClose={handleCloseUsageStats}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Usage statistics — {resolveBusinessName(usageStatsDialogAdmin)}
          </DialogTitle>
          <DialogContent dividers>
            {usageStatsDialogAdmin && (() => {
              const stats = usageStatsByAdmin[usageStatsDialogAdmin.id];
              const period = stats?.periods?.[usageStatsDialogPeriod] || {};
              const activityMeta =
                ACTIVITY_TIER_META[stats?.activityTier] || ACTIVITY_TIER_META.never;
              const enabledFeatures = usageStatsDialogAdmin.features
                ?.filter((f) => f.is_enabled)
                .map((f) => f.feature_name) || [];

              return (
                <Box>
                  <Tabs
                    value={usageStatsDialogPeriod}
                    onChange={(event, value) => setUsageStatsDialogPeriod(value)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{ mb: 2, borderBottom: '1px solid #e5e7eb' }}
                  >
                    {USAGE_PERIOD_TABS.map((tab) => (
                      <Tab key={tab.key} value={tab.key} label={tab.label} />
                    ))}
                  </Tabs>

                  {!stats ? (
                    <Alert severity="info">No usage data loaded for this business admin.</Alert>
                  ) : (
                    <>
                      <Chip
                        label={activityMeta.label}
                        color={activityMeta.color}
                        size="small"
                        sx={{ mb: 2 }}
                      />
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">
                            Bills
                          </Typography>
                          <Typography variant="h6">{period.billCount || 0}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">
                            Bill total
                          </Typography>
                          <Typography variant="h6">
                            {formatUsageCurrency(period.totalAmount)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">
                            Service / product charges
                          </Typography>
                          <Typography variant="h6">
                            {formatUsageCurrency(period.serviceCharges)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">
                            Cancelled bills
                          </Typography>
                          <Typography variant="h6">{period.cancelledCount || 0}</Typography>
                        </Grid>
                      </Grid>

                      <Divider sx={{ my: 2 }} />

                      <Stack spacing={1}>
                        <Typography variant="body2">
                          <strong>Last bill:</strong> {formatUsageDateTime(stats.lastBillAt)}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Last login:</strong> {formatUsageDateTime(stats.lastLogin)}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Operators:</strong> {stats.operatorsCount || 0}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Coupon templates:</strong> {stats.couponsTotal || 0} total
                          {stats.couponsActive != null
                            ? ` · ${stats.couponsActive} active`
                            : ''}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Features enabled:</strong>{' '}
                          {stats.enabledFeaturesCount ?? enabledFeatures.length}
                        </Typography>
                      </Stack>

                      {enabledFeatures.length > 0 && (
                        <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {enabledFeatures.map((feature) => (
                            <Chip key={feature} label={feature} size="small" variant="outlined" />
                          ))}
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              );
            })()}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseUsageStats}>Close</Button>
          </DialogActions>
        </Dialog>
      </Container>
    </DashboardLayout>
  );
};

export default FranchiseeAdminApproval;















